import type { JSX } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';
import { Box, Container } from '@change-corgi/design-system-legacy/layout';

import { SetPageStateValue } from 'src/shared/pageState';

import {
	usePetitionDetailsPageData,
	usePetitionDetailsPageUserData,
} from 'src/app/pages/petitionGamma/details/pageContext';
import { ResponsiveBox, SectionSeparator } from 'src/app/pages/petitionGamma/details/shared/components';
import { BeforeSuggestedPetitionsWaypoint } from 'src/app/pages/petitionGamma/details/shared/waypoints';
import { AppDownloadBottomBanner } from 'src/app/shared/components/appDownloadBottomBanner';
import { PetitionUnderReviewBanner } from 'src/app/shared/components/banners/petitionUnderReview';
import { MembershipBottomBanner } from 'src/app/shared/components/membershipBottomBanner';
import { SuggestedPetitionsLazy } from 'src/app/shared/components/suggestedPetitionsLazy';
import { SuggestedPetitionsLazy as SuggestedPetitionsLazySunrise } from 'src/app/shared/components/suggestedPetitionsLazySunrise';

import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { usePetitionPageStateData } from '../hooks/pageStateData';
import { useTrackPromotedView } from '../hooks/promotions';
import { PageScrollPositionProvider } from '../hooks/scroll';
import { useShowNewlyCreatedPetitions } from '../hooks/showNewlyCreatedPetitions';
import { useShowSuggestedVictoriousPetitions } from '../hooks/showSuggestedVictoriousPetitions';
import { useTrackPageView } from '../hooks/tracking/pageView';

import { DmFeedBannerContainer } from './DmFeedBanner';
import { ExpiredSessionBanner } from './ExpiredSessionBanner';
import { HeadContainer } from './head/Head';
import { HeadingContainer } from './Heading';
import { MainContainer } from './Main';
import { NativeShareMobile } from './NativeShareMobile';
import { RedirectUnpublished } from './RedirectUnpublished';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { StrengthTips } from './strengthTips/StrengthTips';

export function DetailsContainer(): JSX.Element {
	useTrackPageView();
	useTrackPromotedView();
	const showSuggestedVictoriousPetitions = useShowSuggestedVictoriousPetitions();
	const showNewlyCreated = useShowNewlyCreatedPetitions();
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const petitionPageStateData = usePetitionPageStateData();
	const {
		data: { isMobileAppDownloadBannerEnabled, ownPetition },
	} = usePetitionDetailsPageUserData();
	const underReview = petition.status === 'UNDER_REVIEW';

	const {
		data: { isSunriseHeadingEnabled, isSunrisePromotedPetitions },
	} = useIsFullSunriseEnabled();

	return (
		<Box data-testid="details-container" mb={32}>
			<ExpiredSessionBanner />
			{underReview && <PetitionUnderReviewBanner />}
			<RedirectUnpublished />
			{/* FIXME: for consistency this should be rendered in DetailsWithContext with the other providers. */}
			<PageScrollPositionProvider>
				<SetPageStateValue name="petition" value={petitionPageStateData} />
				<HeadContainer />
				<DmFeedBannerContainer />
				<StrengthTips />
				<NativeShareMobile />
				<Container>
					<ResponsiveBox viewportSize="normal">
						{/* Replace the ifTrue expression with the Sunrise version */}
						{isSunriseHeadingEnabled ? <HeadingContainer /> : <HeadingContainer />}
					</ResponsiveBox>
					<MainContainer />
				</Container>
				<BeforeSuggestedPetitionsWaypoint />
				<ScrollTrackingWaypointContainer name="scroll_other_petitions_visible" />
				<Container>
					<SectionSeparator />
				</Container>
				{isSunrisePromotedPetitions ? (
					<SuggestedPetitionsLazySunrise
						showVictorious={showSuggestedVictoriousPetitions}
						showNewlyCreated={showNewlyCreated}
						petitionSlugOrId={petition.id}
						channel="petition_footer"
						sourceLocation="petition_footer"
					/>
				) : (
					<SuggestedPetitionsLazy
						showVictorious={showSuggestedVictoriousPetitions}
						showNewlyCreated={showNewlyCreated}
						petitionSlugOrId={petition.id}
						channel="petition_footer"
						sourceLocation="petition_footer"
					/>
				)}
			</PageScrollPositionProvider>

			<MembershipBottomBanner slug={petition.slug} sourceLocation="petition_membership_popup" />
			<TrackingContext properties={{ petition_id: `petition_${petition.id}` }}>
				<AppDownloadBottomBanner
					sourceLocation="petition"
					ownPetition={ownPetition}
					enabled={isMobileAppDownloadBannerEnabled}
				/>
			</TrackingContext>
		</Box>
	);
}
